// FieldIQ — Activities tab — events, walks, maps

import { useState, useEffect, useCallback } from "react";
import {
  Loader2,
  Shield,
  MapPin,
  ChevronRight,
  ChevronLeft,
  Plus,
  Calendar,
  FileText,
  Mic,
  Map,
  Navigation,
  QrCode,
  Check,
  X,
  UserPlus,
  ExternalLink,
  Camera,
  WifiOff,
  Clock,
} from "lucide-react";
import { DoorKnockEnrollFlow, VoterVoiceFlow } from "../components/EnrollFlows";
import { RichText } from "../components/RichText";
import { EventRsvpButtons, useEventRsvp } from "../components/EventRsvp";
import { askAboutPerson } from "../lib/askAbout";
import { LogInteractionModal } from "../components/LogInteraction";
import { FIELDIQ_API_URL, authFetch, getToken } from "../lib/api";
import { formatActivityDate, localYmd } from "../lib/helpers";
import {
  loadStopsFromCache,
  loadWalksFromCache,
  queueOfflineWrite,
  saveStopsToCache,
  saveWalksToCache,
} from "../lib/offline";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Static Maps API — real map when route is planned, SVG grid when no route
const WalkPathMap = ({ waypoints, polyline }) => {
  const [imgError, setImgError] = useState(false);

  const hasRoute = Array.isArray(waypoints) && waypoints.length >= 2;

  if (hasRoute) {
    if (MAPS_KEY && !imgError) {
      let src = `https://maps.googleapis.com/maps/api/staticmap?key=${MAPS_KEY}&size=640x300&scale=2&maptype=roadmap`;
      if (polyline) {
        src += `&path=color:0x22d3eeff|weight:4|enc:${encodeURIComponent(polyline)}`;
      }
      const stops = waypoints.slice(0, 10);
      stops.forEach((w, i) => {
        const color =
          i === 0 ? "green" : i === stops.length - 1 ? "red" : "blue";
        const label = i === 0 ? "S" : i === stops.length - 1 ? "E" : String(i);
        src += `&markers=color:${color}|label:${label}|${w.lat},${w.lng}`;
      });
      return (
        <div className="rounded-2xl overflow-hidden border border-slate-700/60 bg-slate-950">
          <img
            src={src}
            alt="walk route map"
            style={{ display: "block", width: "100%", height: "auto" }}
            onError={() => setImgError(true)}
          />
        </div>
      );
    }
    // Route exists but map key not available — don't show a misleading fake map
    return null;
  }

  // No route planned — illustrated street-grid placeholder as a teaser
  return (
    <div className="rounded-2xl overflow-hidden border border-slate-700/60 bg-slate-950">
      <svg
        viewBox="0 0 320 200"
        className="w-full"
        style={{ display: "block" }}
        aria-label="Route path map"
      >
        {/* Horizontal roads */}
        <rect x="0" y="36" width="320" height="16" fill="#1e293b" />
        <rect x="0" y="96" width="320" height="16" fill="#1e293b" />
        <rect x="0" y="156" width="320" height="16" fill="#1e293b" />
        {/* Vertical roads */}
        <rect x="56" y="0" width="16" height="200" fill="#1e293b" />
        <rect x="156" y="0" width="16" height="200" fill="#1e293b" />
        <rect x="256" y="0" width="16" height="200" fill="#1e293b" />
        {/* Center-line dashes */}
        <line
          x1="0"
          y1="44"
          x2="320"
          y2="44"
          stroke="#334155"
          strokeWidth="0.5"
          strokeDasharray="6,4"
        />
        <line
          x1="0"
          y1="104"
          x2="320"
          y2="104"
          stroke="#334155"
          strokeWidth="0.5"
          strokeDasharray="6,4"
        />
        <line
          x1="0"
          y1="164"
          x2="320"
          y2="164"
          stroke="#334155"
          strokeWidth="0.5"
          strokeDasharray="6,4"
        />
        <line
          x1="64"
          y1="0"
          x2="64"
          y2="200"
          stroke="#334155"
          strokeWidth="0.5"
          strokeDasharray="6,4"
        />
        <line
          x1="164"
          y1="0"
          x2="164"
          y2="200"
          stroke="#334155"
          strokeWidth="0.5"
          strokeDasharray="6,4"
        />
        <line
          x1="264"
          y1="0"
          x2="264"
          y2="200"
          stroke="#334155"
          strokeWidth="0.5"
          strokeDasharray="6,4"
        />
        {/* Street labels */}
        <text x="2" y="33" fill="#475569" fontSize="5.5" fontFamily="system-ui">
          E Baseline Rd
        </text>
        <text x="2" y="93" fill="#475569" fontSize="5.5" fontFamily="system-ui">
          E Southern Ave
        </text>
        <text
          x="2"
          y="153"
          fill="#475569"
          fontSize="5.5"
          fontFamily="system-ui"
        >
          E Guadalupe Rd
        </text>
        <text x="58" y="11" fill="#475569" fontSize="5" fontFamily="system-ui">
          S Greenfield
        </text>
        <text x="158" y="11" fill="#475569" fontSize="5" fontFamily="system-ui">
          S Gilbert
        </text>
        <text x="258" y="11" fill="#475569" fontSize="5" fontFamily="system-ui">
          S Lindsay
        </text>
        {/* Walk path */}
        <polyline
          points="64,44 164,44 164,104 64,104 64,164 164,164 264,164"
          stroke="#06b6d4"
          strokeWidth="2.5"
          fill="none"
          strokeDasharray="6,3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Door target markers */}
        <circle
          cx="114"
          cy="36"
          r="4"
          fill="#0f172a"
          stroke="#f59e0b"
          strokeWidth="1.5"
        />
        <circle
          cx="164"
          cy="70"
          r="4"
          fill="#0f172a"
          stroke="#f59e0b"
          strokeWidth="1.5"
        />
        <circle
          cx="114"
          cy="96"
          r="4"
          fill="#0f172a"
          stroke="#f59e0b"
          strokeWidth="1.5"
        />
        <circle
          cx="64"
          cy="130"
          r="4"
          fill="#0f172a"
          stroke="#f59e0b"
          strokeWidth="1.5"
        />
        <circle
          cx="114"
          cy="156"
          r="4"
          fill="#0f172a"
          stroke="#f59e0b"
          strokeWidth="1.5"
        />
        <circle
          cx="164"
          cy="156"
          r="4"
          fill="#0f172a"
          stroke="#f59e0b"
          strokeWidth="1.5"
        />
        <circle
          cx="214"
          cy="156"
          r="4"
          fill="#0f172a"
          stroke="#f59e0b"
          strokeWidth="1.5"
        />
        <circle
          cx="264"
          cy="156"
          r="4"
          fill="#0f172a"
          stroke="#f59e0b"
          strokeWidth="1.5"
        />
        {/* End marker */}
        <circle cx="264" cy="164" r="5" fill="#06b6d4" />
        {/* Start marker */}
        <circle cx="64" cy="44" r="6" fill="#10b981" />
        <text
          x="73"
          y="40"
          fill="#10b981"
          fontSize="6.5"
          fontWeight="bold"
          fontFamily="system-ui"
        >
          START
        </text>
      </svg>
      {/* Legend */}
      <div className="flex items-center gap-5 px-3 py-2 border-t border-slate-800">
        <div className="flex items-center gap-1.5">
          <svg width="16" height="4">
            <line
              x1="0"
              y1="2"
              x2="16"
              y2="2"
              stroke="#06b6d4"
              strokeWidth="2"
              strokeDasharray="4,2"
            />
          </svg>
          <span className="text-[10px] text-slate-400">Route path</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full border border-amber-400 bg-slate-950" />
          <span className="text-[10px] text-slate-400">Door targets</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-emerald-400" />
          <span className="text-[10px] text-slate-400">Start</span>
        </div>
      </div>
    </div>
  );
};

const STOP_OUTCOMES = [
  {
    key: "answered",
    label: "Answered",
    cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  },
  {
    key: "not_home",
    label: "Not Home",
    cls: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  },
  {
    key: "refused",
    label: "Refused",
    cls: "bg-red-500/15 border-red-500/40 text-red-300",
  },
  {
    key: "callback",
    label: "Callback",
    cls: "bg-blue-500/15 border-blue-500/40 text-blue-300",
  },
];
const STOP_BADGE = {
  answered: "bg-emerald-500/20 text-emerald-300",
  not_home: "bg-amber-500/20 text-amber-300",
  refused: "bg-red-500/20 text-red-300",
  callback: "bg-blue-500/20 text-blue-300",
};

function VoterInfoPanel({ address, lat, lng }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getToken();
        const headers = {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        const res =
          lat != null && lng != null
            ? await fetch(`${FIELDIQ_API_URL}/api/v1/lookup/coords`, {
                method: "POST",
                headers,
                body: JSON.stringify({ lat, lng }),
              })
            : await fetch(
                `${FIELDIQ_API_URL}/api/v1/lookup/address?address=${encodeURIComponent(address)}`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
        if (cancelled) return;
        if (res.ok) {
          setData(await res.json());
        } else {
          setError(true);
        }
      } catch {
        if (!cancelled) setError(true);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [address, lat, lng]);

  if (loading) {
    return (
      <div className="ml-7 mt-2 text-[10px] text-slate-500 animate-pulse">
        Loading district info…
      </div>
    );
  }

  if (error || !data) return null;

  const { districts = {}, representatives = {} } = data;

  // District pills: label → display name
  const districtRows = [
    { label: "State House", d: districts.state_house },
    { label: "State Senate", d: districts.state_senate },
    { label: "U.S. Congress", d: districts.congressional },
    { label: "School District", d: districts.school_unified },
  ].filter(({ d }) => d?.name || d?.number);

  // Flat rep list: [ { role, name, party } ]
  const repRows = [
    ...(representatives.state_house || []).map((r) => ({
      role: "State House",
      ...r,
    })),
    ...(representatives.state_senate || []).map((r) => ({
      role: "State Senate",
      ...r,
    })),
    ...(representatives.us_house || []).map((r) => ({
      role: "U.S. House",
      ...r,
    })),
    ...(representatives.school_board || []).map((r) => ({
      role: r.seat || "School Board",
      ...r,
    })),
  ];

  if (districtRows.length === 0 && repRows.length === 0) return null;

  return (
    <div className="ml-7 mt-2 space-y-2 text-xs">
      {/* ── Voting precinct ── */}
      {districts.voting_precinct && (
        <div className="bg-slate-900/50 border border-slate-700/40 rounded-lg px-2.5 py-2">
          <div className="text-[10px] font-semibold text-violet-300 uppercase tracking-wide mb-1">
            📍 Voting Precinct
          </div>
          <div className="text-slate-200 font-medium">
            {districts.voting_precinct.name ||
              `Precinct ${districts.voting_precinct.number}`}
          </div>
        </div>
      )}

      {/* ── Voting districts ── */}
      {districtRows.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-700/40 rounded-lg px-2.5 py-2">
          <div className="text-[10px] font-semibold text-violet-300 uppercase tracking-wide mb-1.5">
            🗳 Voting Districts
          </div>
          <div className="space-y-1">
            {districtRows.map(({ label, d }, i) => (
              <div
                key={i}
                className="flex items-baseline justify-between gap-2"
              >
                <span className="text-slate-400 text-[10px]">{label}</span>
                <span className="text-slate-200 font-medium text-right">
                  {d.name || `District ${d.number}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Current representatives ── */}
      {repRows.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-700/40 rounded-lg px-2.5 py-2">
          <div className="text-[10px] font-semibold text-violet-300 uppercase tracking-wide mb-1.5">
            👤 Current Representatives
          </div>
          <div className="space-y-1.5">
            {repRows.map(({ role, name, party }, i) => (
              <div
                key={i}
                className="flex items-baseline justify-between gap-2"
              >
                <div className="text-slate-200 font-medium truncate">
                  <button
                    onClick={() => askAboutPerson(name)}
                    className="hover:text-violet-300 transition-colors text-left"
                  >
                    {name}
                  </button>
                  {party && (
                    <span className="ml-1 text-slate-500 font-normal">
                      ({party})
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 text-right shrink-0 max-w-[45%] leading-snug">
                  {role}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ActivityDetailView = ({
  activity,
  onBack,
  user,
  facility,
  onWalkUpdate,
  isOnline,
}) => {
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [patching, setPatching] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [stopOutcomes, setStopOutcomes] = useState({});
  const [stopMeta, setStopMeta] = useState({}); // {index: {note, photoUrl, photoPreview}}
  const isWalk = activity.type === "walk";
  const isCompleted = activity.status === "completed";
  const isInProgress = activity.status === "in_progress";
  const hasPlannedRoute =
    isWalk &&
    Array.isArray(activity.waypoints) &&
    activity.waypoints.length >= 2;

  // Load saved stop outcomes when this walk is opened
  useEffect(() => {
    if (!hasPlannedRoute || !activity._apiId) return;
    const applyStops = (data) => {
      if (!Array.isArray(data)) return;
      const outcomes = {};
      const meta = {};
      data.forEach((s) => {
        if (s.stop_index != null && s.outcome) {
          outcomes[s.stop_index] = s.outcome;
          if (s.note || s.photo_url)
            meta[s.stop_index] = {
              note: s.note || "",
              photoUrl: s.photo_url || "",
              photoPreview: s.photo_url || "",
            };
        }
      });
      setStopOutcomes(outcomes);
      setStopMeta(meta);
    };
    authFetch(`/fieldiq/walks/${activity._apiId}/stops`)
      .then((data) => {
        if (Array.isArray(data)) {
          applyStops(data);
          saveStopsToCache(activity._apiId, data);
        }
      })
      .catch(async () => {
        const cached = await loadStopsFromCache(activity._apiId);
        if (cached) applyStops(cached);
      });
  }, [activity._apiId, hasPlannedRoute]);

  const compressPhoto = (file) =>
    new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const MAX = 600;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas
          .getContext("2d")
          .drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.6));
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });

  const handleStopPhoto = async (stopIndex, file) => {
    if (!file) return;
    const compressed = await compressPhoto(file);
    if (compressed) {
      setStopMeta((prev) => ({
        ...prev,
        [stopIndex]: {
          ...(prev[stopIndex] || {}),
          photoUrl: compressed,
          photoPreview: compressed,
        },
      }));
    }
  };

  const logStop = async (stopIndex, outcome) => {
    if (patching) return;
    const meta = stopMeta[stopIndex] || {};
    setStopOutcomes((prev) => ({ ...prev, [stopIndex]: outcome }));
    const stop = activity.waypoints?.[stopIndex];
    const body = JSON.stringify({
      stop_index: stopIndex,
      address: stop?.address || "",
      outcome,
      contact_name: stop?.contact_name || null,
      note: meta.note || null,
      photo_url: meta.photoUrl || null,
      facility_id: facility?.facility_id || null,
    });
    if (!isOnline) {
      await queueOfflineWrite(
        `/fieldiq/walks/${activity._apiId}/stops`,
        "POST",
        body,
      );
      return;
    }
    try {
      await authFetch(`/fieldiq/walks/${activity._apiId}/stops`, {
        method: "POST",
        body,
      });
    } catch {}
  };

  const patchWalk = async (patch) => {
    if (!activity._apiId || patching) return;
    setPatching(true);
    if (onWalkUpdate) onWalkUpdate(patch);
    const body = JSON.stringify(patch);
    if (!isOnline) {
      await queueOfflineWrite(
        `/fieldiq/walks/${activity._apiId}`,
        "PATCH",
        body,
      );
      setPatching(false);
      return;
    }
    try {
      await authFetch(`/fieldiq/walks/${activity._apiId}`, {
        method: "PATCH",
        body,
      });
    } catch {}
    setPatching(false);
  };

  const handleStartWalk = async () => {
    await patchWalk({ status: "in_progress" });
  };

  const logDoor = async (result) => {
    if (patching) return;
    const noteMap = {
      answered: "Door answered",
      not_home: "Not home",
      skip: "Skipped",
    };
    const patch =
      result === "answered"
        ? {
            stops_visited: (activity.completed ?? 0) + 1,
            stops_contacted: (activity.answered ?? 0) + 1,
          }
        : { stops_visited: (activity.completed ?? 0) + 1 };
    if (!isOnline) {
      await queueOfflineWrite(
        "/fieldiq/interactions",
        "POST",
        JSON.stringify({
          interaction_type: "door",
          notes: noteMap[result] || result,
          facility_id: facility?.facility_id || null,
        }),
      );
      await patchWalk(patch);
      return;
    }
    try {
      await authFetch("/fieldiq/interactions", {
        method: "POST",
        body: JSON.stringify({
          interaction_type: "door",
          notes: noteMap[result] || result,
          facility_id: facility?.facility_id || null,
        }),
      });
    } catch {}
    await patchWalk(patch);
  };

  const openMaps = () => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.address)}`,
    );
  };

  const buildCalDates = () => {
    let start;
    if (activity.date && activity.time) {
      start = new Date(`${activity.date} ${activity.time}`);
    } else if (activity.date) {
      start = new Date(`${activity.date}T09:00:00`);
    } else {
      start = new Date();
    }
    if (isNaN(start)) start = new Date();
    const end = new Date(start.getTime() + (isWalk ? 2 : 1) * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    const fmtDt = (d) =>
      `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    return { start, end, fmtDt };
  };

  const openGoogleCal = () => {
    const { start, end, fmtDt } = buildCalDates();
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: activity.title || "",
      dates: `${fmtDt(start)}/${fmtDt(end)}`,
      details: activity.description || "",
      location: activity.location || "",
    });
    window.open(
      `https://calendar.google.com/calendar/render?${params}`,
      "_blank",
    );
    setCalOpen(false);
  };

  const downloadIcs = () => {
    const { start, end, fmtDt } = buildCalDates();
    const esc = (s) =>
      (s || "").replace(/[,;\\]/g, "\\$&").replace(/\n/g, "\\n");
    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//FieldIQ//FieldIQ//EN",
      "BEGIN:VEVENT",
      `UID:${Date.now()}-${activity.id}@fieldiq`,
      `DTSTAMP:${fmtDt(new Date())}`,
      `DTSTART:${fmtDt(start)}`,
      `DTEND:${fmtDt(end)}`,
      `SUMMARY:${esc(activity.title)}`,
      activity.description ? `DESCRIPTION:${esc(activity.description)}` : null,
      activity.location ? `LOCATION:${esc(activity.location)}` : null,
      "END:VEVENT",
      "END:VCALENDAR",
    ].filter(Boolean);
    const blob = new Blob([lines.join("\r\n")], {
      type: "text/calendar;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(activity.title || "event").replace(/[^a-z0-9]/gi, "_")}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setCalOpen(false);
  };

  const typeLabel = isWalk
    ? "Route"
    : activity.subtype === "vote"
      ? "Vote"
      : "Meeting";

  const typeColors = isWalk
    ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
    : activity.subtype === "vote"
      ? "bg-rose-500/15 text-rose-300 border-rose-500/30"
      : "bg-blue-500/15 text-blue-300 border-blue-500/30";

  if (enrollOpen) {
    return (
      <DoorKnockEnrollFlow
        address={activity.location}
        facility={facility}
        onDone={() => setEnrollOpen(false)}
      />
    );
  }

  if (voiceOpen) {
    return (
      <VoterVoiceFlow
        onDone={() => setVoiceOpen(false)}
        facility={facility}
        user={user}
      />
    );
  }

  if (logOpen) {
    return (
      <LogInteractionModal
        onClose={() => setLogOpen(false)}
        onLog={() => {
          patchWalk({ stops_visited: (activity.completed ?? 0) + 1 });
          setLogOpen(false);
        }}
        user={user}
        facility={facility}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Back */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300"
      >
        <ChevronLeft size={16} /> Activities
      </button>

      {/* Title block */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border ${typeColors}`}
          >
            {typeLabel}
          </span>
          {isCompleted && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
              Completed
            </span>
          )}
        </div>
        <h1 className="text-xl font-bold text-white leading-snug">
          {activity.title}
        </h1>
      </div>

      {/* Info card */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Calendar size={15} className="text-cyan-400 flex-shrink-0" />
          <div>
            <div className="text-white text-sm font-medium">
              {formatActivityDate(activity.date)}
            </div>
            <div className="text-slate-400 text-xs">{activity.time}</div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <MapPin size={15} className="text-cyan-400 flex-shrink-0 mt-0.5" />
          <div className="text-white text-sm">{activity.location}</div>
        </div>
        <div className="pt-2 border-t border-slate-700/60">
          <RichText
            text={activity.description}
            className="text-slate-300 text-sm leading-relaxed"
          />
        </div>
      </div>

      {/* Walk: door progress + map */}
      {isWalk && (
        <>
          {/* ── Planned-route stop list (any walk with waypoints) ── */}
          {hasPlannedRoute && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Route Stops
                </span>
                {isInProgress && (
                  <span className="text-xs text-cyan-400">
                    {Object.keys(stopOutcomes).length} /{" "}
                    {activity.waypoints.length} done
                  </span>
                )}
              </div>
              {activity.waypoints.map((stop, i) => {
                const outcome = stopOutcomes[i];
                return (
                  <div
                    key={i}
                    className={`bg-slate-800/40 border rounded-xl p-3 transition-colors ${
                      outcome
                        ? "border-slate-700/50 opacity-80"
                        : "border-cyan-500/20"
                    }`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <span
                        className={`w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          outcome ? "bg-slate-600" : "bg-blue-600"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-slate-200 leading-snug block">
                          {stop.address}
                        </span>
                        {stop.contact_name && (
                          <span className="text-xs text-cyan-400 mt-0.5 block">
                            {stop.contact_name}
                          </span>
                        )}
                      </div>
                      {outcome && (
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${STOP_BADGE[outcome] || "bg-slate-700 text-slate-300"}`}
                        >
                          {STOP_OUTCOMES.find((o) => o.key === outcome)
                            ?.label || outcome}
                        </span>
                      )}
                    </div>
                    {/* Logged meta: note + photo thumbnail */}
                    {outcome && stopMeta[i] && (
                      <div className="ml-7 space-y-1.5 mt-1">
                        {stopMeta[i].note && (
                          <p className="text-xs text-slate-400 leading-relaxed">
                            {stopMeta[i].note}
                          </p>
                        )}
                        {stopMeta[i].photoPreview && (
                          <img
                            src={stopMeta[i].photoPreview}
                            alt="Stop photo"
                            className="h-20 rounded-lg object-cover border border-slate-700"
                          />
                        )}
                      </div>
                    )}
                    {isInProgress && !outcome && (
                      <div className="ml-7 space-y-2">
                        {/* Note field */}
                        <textarea
                          rows={2}
                          value={stopMeta[i]?.note || ""}
                          onChange={(e) =>
                            setStopMeta((prev) => ({
                              ...prev,
                              [i]: { ...(prev[i] || {}), note: e.target.value },
                            }))
                          }
                          placeholder="Add a note… (optional)"
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none resize-none"
                        />
                        {/* Photo */}
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-700/60 border border-slate-600 text-slate-300 text-[10px] font-semibold cursor-pointer hover:border-slate-500 transition-colors">
                            <Camera size={11} />
                            {stopMeta[i]?.photoPreview
                              ? "Change Photo"
                              : "Add Photo"}
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              onChange={(e) =>
                                handleStopPhoto(i, e.target.files?.[0])
                              }
                            />
                          </label>
                          {stopMeta[i]?.photoPreview && (
                            <img
                              src={stopMeta[i].photoPreview}
                              alt="Preview"
                              className="h-8 w-8 rounded object-cover border border-slate-600"
                            />
                          )}
                        </div>
                        {/* Outcome buttons */}
                        <div className="grid grid-cols-4 gap-1">
                          {STOP_OUTCOMES.map(({ key, label, cls }) => (
                            <button
                              key={key}
                              onClick={() => logStop(i, key)}
                              disabled={patching}
                              className={`py-2 rounded-lg border text-[10px] font-semibold disabled:opacity-40 ${cls}`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {stop.address && (
                      <VoterInfoPanel
                        address={stop.address}
                        lat={stop.lat}
                        lng={stop.lng}
                      />
                    )}
                  </div>
                );
              })}
              {isInProgress && (
                <div className="flex items-center gap-2 pt-1">
                  <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full transition-all"
                      style={{
                        width: `${Math.round(
                          (Object.keys(stopOutcomes).length /
                            activity.waypoints.length) *
                            100,
                        )}%`,
                      }}
                    />
                  </div>
                  <span className="text-xs text-slate-400">
                    {Math.round(
                      (Object.keys(stopOutcomes).length /
                        activity.waypoints.length) *
                        100,
                    )}
                    %
                  </span>
                </div>
              )}
            </div>
          )}

          {isInProgress && !hasPlannedRoute ? (
            /* ── Active walk without planned route: generic door counter ── */
            <div className="bg-slate-800/40 border border-cyan-500/30 rounded-2xl p-5">
              <div className="text-center mb-4">
                <div className="text-5xl font-black text-cyan-300 tabular-nums">
                  {activity.completed ?? 0}
                </div>
                <div className="text-xs text-slate-400 mt-1 uppercase tracking-wide">
                  doors knocked
                  {activity.answered != null
                    ? ` · ${activity.answered} answered`
                    : ""}
                </div>
              </div>

              {/* Quick-log buttons */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <button
                  onClick={() => logDoor("answered")}
                  disabled={patching}
                  className="py-3 flex flex-col items-center gap-1 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-semibold disabled:opacity-40 hover:border-emerald-400/60 transition-colors"
                >
                  <Check size={18} />
                  Answered
                </button>
                <button
                  onClick={() => logDoor("not_home")}
                  disabled={patching}
                  className="py-3 flex flex-col items-center gap-1 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-semibold disabled:opacity-40 hover:border-amber-400/60 transition-colors"
                >
                  <X size={18} />
                  Not Home
                </button>
                <button
                  onClick={() => logDoor("skip")}
                  disabled={patching}
                  className="py-3 flex flex-col items-center gap-1 rounded-xl bg-slate-700/60 border border-slate-600 text-slate-300 text-xs font-semibold disabled:opacity-40 hover:border-slate-500 transition-colors"
                >
                  <ChevronRight size={18} />
                  Skip
                </button>
              </div>

              {/* Secondary at-door actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => setLogOpen(true)}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600/15 border border-blue-500/40 text-blue-300 text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-blue-400/60 transition-colors"
                >
                  <FileText size={13} /> Log Interaction
                </button>
                <button
                  onClick={() => setEnrollOpen(true)}
                  className="flex-1 py-2.5 rounded-lg bg-emerald-600/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-emerald-400/60 transition-colors"
                >
                  <UserPlus size={13} /> Enroll
                </button>
                <button
                  onClick={() => setVoiceOpen(true)}
                  className="flex-1 py-2.5 rounded-lg bg-orange-600/10 border border-orange-500/30 text-orange-300 text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-orange-400/60 transition-colors"
                >
                  <Mic size={13} /> Voice
                </button>
              </div>
            </div>
          ) : (
            /* ── Scheduled / completed: progress bar display ── */
            <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-white">
                  Door Progress
                </span>
                <span className="text-sm font-bold text-cyan-300">
                  {activity.completed ?? 0}
                  {activity.doors != null ? ` / ${activity.doors}` : " knocked"}
                </span>
              </div>
              <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full transition-all"
                  style={{
                    width: activity.doors
                      ? `${Math.min(100, Math.round(((activity.completed ?? 0) / activity.doors) * 100))}%`
                      : "0%",
                  }}
                />
              </div>
              {activity.doors != null ? (
                <p className="text-xs text-slate-500 mt-2">
                  {Math.max(0, activity.doors - (activity.completed ?? 0))}{" "}
                  doors remaining
                </p>
              ) : (
                <p className="text-xs text-slate-500 mt-2">
                  {activity.answered ?? 0} answered
                </p>
              )}
            </div>
          )}

          <WalkPathMap
            waypoints={activity.waypoints}
            polyline={activity.route_polyline}
          />

          {/* Navigate button — shown when route_url is set */}
          {activity.route_url && (
            <a
              href={activity.route_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-sm font-semibold hover:bg-cyan-500/20 transition-colors"
            >
              <MapPin size={15} />
              Navigate Route in Google Maps
            </a>
          )}

          {/* No route message — when walk has no planned route and no URL */}
          {!hasPlannedRoute && !activity.route_url && (
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4 text-center">
              <p className="text-slate-400 text-xs">
                No route planned for this walk. Build a route in the admin
                portal to see stop-by-stop navigation here.
              </p>
            </div>
          )}

          {/* Pre-walk CTAs (scheduled, not yet started) */}
          {!isCompleted && !isInProgress && (
            <div className="flex gap-2">
              <button
                onClick={() => setEnrollOpen(true)}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-500/20 to-teal-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-emerald-400/60 transition-colors"
              >
                <QrCode size={14} />
                Enroll Voter
              </button>
              <button
                onClick={() => setVoiceOpen(true)}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#F97316]/15 to-amber-500/5 border border-[#F97316]/40 text-orange-300 text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-[#F97316]/60 transition-colors"
              >
                <Mic size={14} />
                Your Voice
              </button>
            </div>
          )}
        </>
      )}

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={openMaps}
          className="flex-1 flex items-center justify-center gap-2 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-slate-200 text-sm font-semibold hover:border-slate-500 transition-colors"
        >
          <MapPin size={15} /> Directions
        </button>
        {isWalk && isInProgress ? (
          <button
            onClick={() => patchWalk({ status: "completed" })}
            disabled={patching}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-emerald-600 to-teal-700 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
          >
            <Check size={15} /> Complete Route
          </button>
        ) : isWalk && !isCompleted ? (
          <button
            onClick={handleStartWalk}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-cyan-600 to-blue-700 rounded-xl text-white text-sm font-semibold"
          >
            <Navigation size={15} /> Start Route
          </button>
        ) : (
          <button
            onClick={() => setCalOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-cyan-600 to-blue-700 rounded-xl text-white text-sm font-semibold"
          >
            <Calendar size={15} /> Add to Calendar
          </button>
        )}
      </div>
      {isWalk && !isCompleted && (
        <button
          onClick={() => setCalOpen(true)}
          className="w-full flex items-center justify-center gap-2 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-slate-200 text-sm font-semibold hover:border-slate-500 transition-colors"
        >
          <Calendar size={15} /> Add to Calendar
        </button>
      )}

      {/* Calendar picker sheet */}
      {calOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          onClick={() => setCalOpen(false)}
        >
          <div
            className="w-full bg-slate-900 border-t border-slate-700 rounded-t-2xl p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Add to Calendar
            </div>
            <button
              onClick={openGoogleCal}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm font-semibold hover:border-slate-500 transition-colors"
            >
              <span className="text-base">📅</span> Google Calendar
            </button>
            <button
              onClick={downloadIcs}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm font-semibold hover:border-slate-500 transition-colors"
            >
              <span className="text-base">🍎</span> Apple Calendar
            </button>
            <button
              onClick={downloadIcs}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm font-semibold hover:border-slate-500 transition-colors"
            >
              <span className="text-base">📨</span> Outlook
            </button>
            <button
              onClick={() => setCalOpen(false)}
              className="w-full py-3 text-slate-400 text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const mapApiEvent = (e) => ({
  id: `api-ev-${e.id}`,
  _apiId: e.id,
  type: "event",
  subtype: e.event_type === "vote" ? "vote" : "meeting",
  title: e.title,
  // Local calendar date, not the UTC one the string starts with — otherwise
  // an evening event shows tomorrow's date beside this evening's time.
  date: e.start_at ? localYmd(e.start_at) : "",
  time: e.start_at
    ? new Date(e.start_at).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
    : "",
  end_at: e.end_at || null,
  url: e.url || null,
  user_rsvp: e.user_rsvp || null,
  going_count: e.going_count ?? 0,
  maybe_count: e.maybe_count ?? 0,
  location: e.location_name || "",
  address: e.location_address || "",
  description: e.agenda || "",
  status:
    e.start_at && new Date(e.start_at) < new Date() ? "completed" : "upcoming",
});

export const mapApiWalk = (w) => ({
  id: `api-wk-${w.id}`,
  _apiId: w.id,
  type: "walk",
  title: w.title || `Route — ${w.area_subunit || ""}`,
  date: w.scheduled_at ? w.scheduled_at.slice(0, 10) : "",
  time: w.scheduled_at
    ? new Date(w.scheduled_at).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
    : "",
  location: w.area_subunit || w.location || "",
  address: w.area_subunit || w.location || "",
  area_district: w.area_district || "",
  description: w.notes || "",
  status:
    w.status === "completed"
      ? "completed"
      : w.status === "in_progress"
        ? "in_progress"
        : "upcoming",
  doors: w.stops_total || null,
  completed: w.stops_visited || 0,
  answered: w.stops_contacted || 0,
  waypoints: Array.isArray(w.waypoints) ? w.waypoints : [],
  route_polyline: w.route_polyline || null,
  route_url: w.route_url || null,
});

const WALK_TYPE_LABELS_FIQ = {
  door_knock: "Door Knock",
  lit_drop: "Lit Drop",
  phone_bank: "Phone Bank",
  event: "Event",
};

const CreateWalkModal = ({ facility, onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: "",
    date: "",
    type: "door_knock",
    location: "",
    notes: "",
    stops: [],
  });
  const [stopInput, setStopInput] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [geolocating, setGeolocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addStop = (stop) =>
    setForm((f) => ({ ...f, stops: [...f.stops, stop] }));
  const removeStop = (i) =>
    setForm((f) => ({ ...f, stops: f.stops.filter((_, idx) => idx !== i) }));

  const handleAddTyped = async () => {
    const q = stopInput.trim();
    if (!q || geocoding) return;
    setGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`,
        { headers: { "User-Agent": "FieldIQ/1.0" } },
      );
      const data = await res.json();
      if (data[0]) {
        addStop({
          address: data[0].display_name,
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        });
        setStopInput("");
      } else {
        // Store as address-only if geocoding finds nothing
        addStop({ address: q, lat: null, lng: null });
        setStopInput("");
      }
    } catch {
      addStop({ address: q, lat: null, lng: null });
      setStopInput("");
    } finally {
      setGeocoding(false);
    }
  };

  const handleAddLocation = () => {
    if (!navigator.geolocation || geolocating) return;
    setGeolocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        let address = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
            { headers: { "User-Agent": "FieldIQ/1.0" } },
          );
          const data = await res.json();
          if (data.display_name) address = data.display_name;
        } catch {}
        addStop({ address, lat, lng });
        setGeolocating(false);
      },
      () => setGeolocating(false),
      { timeout: 8000 },
    );
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.date || saving) return;
    setSaving(true);
    setError("");
    const res = await authFetch("/fieldiq/walks", {
      method: "POST",
      body: JSON.stringify({
        title: form.title.trim(),
        scheduled_at: form.date,
        walk_type: form.type,
        location: form.location.trim(),
        notes: form.notes.trim(),
        facility_id: facility?.facility_id,
        waypoints: form.stops,
      }),
    });
    setSaving(false);
    if (res.error) {
      setError(res.error);
    } else {
      onCreated(res);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-end justify-center z-50 p-4 pb-6">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-sm max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 flex-shrink-0">
          <h3 className="text-white font-semibold text-sm">Plan a Route</h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-300"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="e.g. Precinct 123 Saturday Route"
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">
                Date *
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setField("date", e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) => setField("type", e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white focus:border-cyan-500 focus:outline-none"
              >
                {Object.entries(WALK_TYPE_LABELS_FIQ).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Meeting Point
            </label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => setField("location", e.target.value)}
              placeholder="e.g. 123 Main St parking lot"
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
            />
          </div>

          {/* Route stops */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-slate-400">
                Route Stops
                {form.stops.length > 0 && (
                  <span className="ml-1.5 bg-cyan-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {form.stops.length}
                  </span>
                )}
              </label>
            </div>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={stopInput}
                onChange={(e) => setStopInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddTyped()}
                placeholder="Search address…"
                className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
              />
              <button
                onClick={handleAddTyped}
                disabled={!stopInput.trim() || geocoding}
                className="px-3 py-2 rounded-xl bg-slate-700 text-slate-300 hover:bg-slate-600 disabled:opacity-40 flex items-center gap-1 text-xs font-medium"
              >
                {geocoding ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Plus size={13} />
                )}
                Add
              </button>
            </div>
            <button
              onClick={handleAddLocation}
              disabled={geolocating}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-slate-700 text-slate-400 hover:text-cyan-400 hover:border-cyan-700 text-xs font-medium transition-colors disabled:opacity-40"
            >
              {geolocating ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Navigation size={13} />
              )}
              {geolocating ? "Getting location…" : "Pin my current location"}
            </button>
            {form.stops.length > 0 && (
              <div className="mt-2 space-y-1">
                {form.stops.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5"
                  >
                    <span className="w-4 h-4 rounded-full bg-cyan-600 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="flex-1 truncate text-slate-300 text-xs">
                      {s.address}
                    </span>
                    {s.lat && (
                      <MapPin
                        size={10}
                        className="text-cyan-500 flex-shrink-0"
                      />
                    )}
                    <button
                      onClick={() => removeStop(i)}
                      className="text-slate-500 hover:text-red-400 flex-shrink-0"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              rows={2}
              placeholder="Bring walking shoes, meet at 9am…"
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none resize-none"
            />
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>
        <div className="flex gap-3 px-5 pb-5 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-sm hover:text-slate-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!form.title.trim() || !form.date || saving}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? "Saving…" : "Create Route"}
          </button>
        </div>
      </div>
    </div>
  );
};

const EventDetailView = ({ activity, user, facility, isOnline, onBack }) => {
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // Shared with the Today tab's Upcoming Events cards — see components/EventRsvp.
  const {
    rsvp,
    goingCount,
    maybeCount,
    loading: rsvpLoading,
    submit: handleRsvp,
  } = useEventRsvp({
    apiId: activity._apiId,
    facilityId: facility?.facility_id,
    isOnline,
    event: activity,
    onToast: showToast,
  });

  const handleCheckIn = async () => {
    if (!isOnline) {
      showToast("Can't check in while offline");
      return;
    }
    setCheckInLoading(true);
    const body = { facility_id: facility?.facility_id };
    if (navigator.geolocation) {
      await new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            body.lat = pos.coords.latitude;
            body.lng = pos.coords.longitude;
            resolve();
          },
          () => resolve(),
          { timeout: 5000, maximumAge: 60000 },
        );
      });
    }
    const res = await authFetch(`/fieldiq/events/${activity._apiId}/checkin`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    setCheckInLoading(false);
    if (!res?.error) {
      setCheckedIn(true);
      showToast("Checked in!");
    } else {
      showToast(res.error || "Check-in failed");
    }
  };

  const startDate = activity.date
    ? new Date(activity.date + "T" + (activity.time ? "" : "00:00"))
    : null;
  const today = new Date().toDateString();
  const isToday = startDate && startDate.toDateString() === today;
  const isOngoing = (() => {
    if (!activity.end_at) return isToday;
    return (
      new Date() >= new Date(activity.date) &&
      new Date() <= new Date(activity.end_at)
    );
  })();

  const iconEl =
    activity.subtype === "vote" ? (
      <Shield size={20} className="text-rose-400" />
    ) : (
      <Calendar size={20} className="text-blue-400" />
    );
  const iconBg =
    activity.subtype === "vote"
      ? "bg-rose-500/15 border-rose-500/30"
      : "bg-blue-500/15 border-blue-500/30";

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white text-sm font-medium shadow-lg">
          {toast}
        </div>
      )}

      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-slate-400 text-sm hover:text-white transition-colors"
      >
        <ChevronLeft size={16} />
        Back
      </button>

      {/* Header */}
      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border ${iconBg}`}
          >
            {iconEl}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-white font-bold text-lg leading-snug">
              {activity.title}
            </h2>
            {activity.subtype && (
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                {activity.subtype === "vote" ? "Vote" : "Meeting"}
              </span>
            )}
          </div>
        </div>

        {/* Date/time */}
        <div className="flex items-center gap-2 text-sm text-slate-300">
          <Clock size={14} className="text-slate-500 flex-shrink-0" />
          <span>
            {formatActivityDate(activity.date)}
            {activity.time && ` · ${activity.time}`}
            {activity.end_at &&
              ` – ${new Date(activity.end_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`}
          </span>
        </div>

        {/* Location */}
        {(activity.location || activity.address) && (
          <div className="flex items-start gap-2 text-sm text-slate-300">
            <MapPin size={14} className="text-slate-500 flex-shrink-0 mt-0.5" />
            <span>{activity.location || activity.address}</span>
          </div>
        )}

        {/* URL */}
        {activity.url && (
          <a
            href={activity.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300"
          >
            <ExternalLink size={14} className="flex-shrink-0" />
            View event details
          </a>
        )}

        {/* Description — ICS feeds carry HTML here; RichText sanitizes it */}
        <RichText
          text={activity.description}
          className="text-sm text-slate-400 leading-relaxed"
        />
      </div>

      {/* RSVP */}
      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-5 space-y-3">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
          RSVP
        </p>
        <EventRsvpButtons
          rsvp={rsvp}
          loading={rsvpLoading}
          onSelect={handleRsvp}
        />
        {(goingCount > 0 || maybeCount > 0) && (
          <p className="text-xs text-slate-500 pt-1">
            {goingCount > 0 && (
              <span className="text-emerald-400 font-semibold">
                {goingCount} going
              </span>
            )}
            {goingCount > 0 && maybeCount > 0 && (
              <span className="text-slate-600"> · </span>
            )}
            {maybeCount > 0 && (
              <span className="text-amber-400 font-semibold">
                {maybeCount} maybe
              </span>
            )}
          </p>
        )}
      </div>

      {/* Check-in */}
      {(isToday || isOngoing) && (
        <button
          onClick={handleCheckIn}
          disabled={checkInLoading || checkedIn}
          className={`w-full py-3 rounded-2xl border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
            checkedIn
              ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
              : "bg-slate-800/60 border-slate-700 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300"
          }`}
        >
          {checkInLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : checkedIn ? (
            <>
              <Check size={16} /> Checked in
            </>
          ) : (
            <>
              <MapPin size={16} /> Check in to this event
            </>
          )}
        </button>
      )}
    </div>
  );
};

export const ActivitiesTab = ({ user, facility, isOnline }) => {
  const [filter, setFilter] = useState("all");
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [communityWalks, setCommunityWalks] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [joiningId, setJoiningId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchMyActivities = useCallback(() => {
    const fid = facility?.facility_id;
    const district = facility?.ld_district;
    const qs = new URLSearchParams();
    if (district) qs.set("district", district);
    if (fid) qs.set("facility_id", fid);
    return authFetch(`/fieldiq/activities?${qs}`)
      .then((data) => {
        if (data && (Array.isArray(data.events) || Array.isArray(data.walks))) {
          const events = (data.events || []).map(mapApiEvent);
          const walks = (data.walks || []).map(mapApiWalk);
          const all = [...events, ...walks];
          setActivities(all);
          saveWalksToCache(walks);
        } else {
          setActivities([]);
        }
      })
      .catch(async () => {
        const cached = await loadWalksFromCache();
        setActivities(cached);
      })
      .finally(() => setLoading(false));
  }, [facility]);

  const fetchCommunityWalks = useCallback(async () => {
    if (!facility?.facility_id) return;
    setCommunityLoading(true);
    const data = await authFetch(
      `/fieldiq/facility-walks?facility_id=${encodeURIComponent(facility.facility_id)}`,
    );
    setCommunityWalks(Array.isArray(data) ? data : []);
    setCommunityLoading(false);
  }, [facility]);

  useEffect(() => {
    fetchMyActivities();
    fetchCommunityWalks();
  }, [fetchMyActivities, fetchCommunityWalks]);

  const handleJoinToggle = async (walk) => {
    setJoiningId(walk.id);
    const method = walk.joined ? "DELETE" : "POST";
    const res = await authFetch(`/fieldiq/walks/${walk.id}/join`, { method });
    if (!res.error) {
      setCommunityWalks((prev) =>
        prev.map((w) =>
          w.id === walk.id ? { ...w, joined: !walk.joined } : w,
        ),
      );
      // Refresh my activities so newly joined walk appears
      if (!walk.joined) fetchMyActivities();
    }
    setJoiningId(null);
  };

  const handleCreated = (res) => {
    setShowCreate(false);
    fetchCommunityWalks();
    fetchMyActivities();
  };

  const updateWalkProgress = (activityId, patch) => {
    setActivities((prev) =>
      prev.map((a) => (a.id === activityId ? { ...a, ...patch } : a)),
    );
  };

  if (selectedActivity) {
    if (selectedActivity.type === "event") {
      return (
        <EventDetailView
          activity={selectedActivity}
          user={user}
          facility={facility}
          isOnline={isOnline}
          onBack={() => setSelectedActivity(null)}
        />
      );
    }
    return (
      <ActivityDetailView
        activity={selectedActivity}
        user={user}
        facility={facility}
        isOnline={isOnline}
        onBack={() => setSelectedActivity(null)}
        onWalkUpdate={(patch) => {
          updateWalkProgress(selectedActivity.id, patch);
          setSelectedActivity((prev) => ({ ...prev, ...patch }));
        }}
      />
    );
  }

  const filtered = activities.filter(
    (a) => filter === "all" || a.type === filter,
  );
  const sorted = [...filtered].sort((a, b) => {
    if (a.status === "completed" && b.status !== "completed") return 1;
    if (b.status === "completed" && a.status !== "completed") return -1;
    return new Date(a.date) - new Date(b.date);
  });

  const upcomingCount = activities.filter(
    (a) => a.status === "upcoming",
  ).length;

  return (
    <div className="space-y-4">
      {showCreate && (
        <CreateWalkModal
          facility={facility}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {!isOnline && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-900/40 border border-amber-700/50 rounded-xl text-amber-300 text-xs">
          <WifiOff size={13} />
          Offline — showing last synced data. Changes will sync when
          reconnected.
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Activities</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            {upcomingCount} upcoming
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl text-white text-xs font-semibold"
        >
          <Plus size={14} />
          Plan Route
        </button>
      </div>

      {/* Community walks */}
      {(communityLoading || communityWalks.length > 0) && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Facility Routes — Join One
          </p>
          {communityLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 size={18} className="animate-spin text-slate-600" />
            </div>
          ) : (
            <div className="space-y-2">
              {communityWalks.map((w) => {
                const names = w.assignees?.map((a) => a.name) || [];
                const goingLabel =
                  names.length === 0
                    ? "No one signed up yet"
                    : names.length <= 2
                      ? names.join(" & ") + " going"
                      : `${names[0]}, ${names[1]} + ${names.length - 2} more`;
                const isJoining = joiningId === w.id;
                return (
                  <div
                    key={w.id}
                    className="bg-slate-800/40 border border-slate-700 rounded-2xl p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                        <Map size={16} className="text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold leading-tight truncate">
                          {w.title}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-cyan-400 font-medium">
                            {formatActivityDate(w.date)}
                          </span>
                          {w.time && (
                            <>
                              <span className="text-slate-600 text-xs">·</span>
                              <span className="text-xs text-slate-400">
                                {w.time}
                              </span>
                            </>
                          )}
                        </div>
                        {w.location && (
                          <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {w.location}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 mt-1">
                          {goingLabel}
                        </p>
                        {w.notes && (
                          <p className="text-xs text-slate-400 mt-1 italic line-clamp-2">
                            {w.notes}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleJoinToggle(w)}
                        disabled={isJoining}
                        className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                          w.joined
                            ? "bg-green-500/20 border border-green-500/40 text-green-400 hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-400"
                            : "bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/30"
                        }`}
                      >
                        {isJoining ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : w.joined ? (
                          <Check size={12} />
                        ) : (
                          <UserPlus size={12} />
                        )}
                        {w.joined ? "In" : "Join"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Filter chips */}
      <div className="flex gap-2">
        {[
          { id: "all", label: "All" },
          { id: "event", label: "Events" },
          { id: "walk", label: "Routes" },
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filter === id
                ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                : "bg-slate-800/60 border border-slate-700 text-slate-400 hover:text-slate-300"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* My activity cards */}
      <div className="space-y-3">
        {!loading && sorted.length === 0 && (
          <div className="text-center py-12 text-slate-500 text-sm">
            No activities scheduled.
          </div>
        )}
        {sorted.map((activity) => {
          const isWalk = activity.type === "walk";
          const isCompleted = activity.status === "completed";
          const iconEl = isWalk ? (
            <Map size={18} className="text-amber-400" />
          ) : activity.subtype === "vote" ? (
            <Shield size={18} className="text-rose-400" />
          ) : (
            <Calendar size={18} className="text-blue-400" />
          );
          const iconBg = isWalk
            ? "bg-amber-500/15 border-amber-500/30"
            : activity.subtype === "vote"
              ? "bg-rose-500/15 border-rose-500/30"
              : "bg-blue-500/15 border-blue-500/30";

          return (
            <button
              key={activity.id}
              onClick={() => setSelectedActivity(activity)}
              className={`w-full rounded-2xl border p-4 text-left transition-colors ${
                isCompleted
                  ? "bg-slate-800/20 border-slate-800 opacity-60"
                  : "bg-slate-800/40 border-slate-700 hover:border-slate-600"
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${iconBg}`}
                >
                  {iconEl}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-white font-semibold text-sm leading-tight">
                      {activity.title}
                    </div>
                    <ChevronRight
                      size={16}
                      className="text-slate-600 flex-shrink-0 mt-0.5"
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-cyan-400 font-medium">
                      {formatActivityDate(activity.date)}
                    </span>
                    <span className="text-slate-600 text-xs">·</span>
                    <span className="text-xs text-slate-400">
                      {activity.time}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {activity.location}
                  </div>
                  {isWalk && (
                    <div className="mt-2.5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-400">
                          {activity.completed ?? 0}
                          {activity.doors != null
                            ? `/${activity.doors} doors`
                            : " knocked"}
                        </span>
                        <span className="text-xs text-slate-500">
                          {activity.doors
                            ? `${Math.min(100, Math.round(((activity.completed ?? 0) / activity.doors) * 100))}%`
                            : ""}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full"
                          style={{
                            width: activity.doors
                              ? `${Math.min(100, Math.round(((activity.completed ?? 0) / activity.doors) * 100))}%`
                              : "0%",
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PROFILE TAB — with photo upload
